import { MapPin, Phone, Mail, Clock, MessageCircle, ArrowUpRight } from "lucide-react";
import { SITE, waLink } from "@/lib/siteConfig";

const HOURS = SITE.hours;

export default function LocationContact() {
  return (
    <section
      id="location"
      className="py-20 lg:py-28 bg-white"
      data-testid="location-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left: info */}
          <div className="lg:col-span-5 flex flex-col">
            <div className="eyebrow mb-4">Visit Us</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
              Drop by.
              <br />
              <span className="text-[#FF3333]">We're nearby.</span>
            </h2>

            <div className="mt-10 space-y-6">
              <a
                href={SITE.googleMapsShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
                data-testid="info-address"
              >
                <div className="flex gap-4">
                  <div className="w-10 h-10 border border-[#0A0A0A] flex items-center justify-center flex-shrink-0 group-hover:bg-[#0A0A0A] group-hover:text-white transition-colors">
                    <MapPin className="w-4 h-4" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="font-mono-ez text-[11px] uppercase tracking-widest text-[#525252]">
                      Address
                    </div>
                    <div className="font-medium text-base mt-1 inline-flex items-center gap-1.5 group-hover:text-[#FF3333] transition-colors">
                      {SITE.addressLines[0]}
                      <ArrowUpRight className="w-4 h-4" />
                    </div>
                    <div className="text-sm text-[#525252] mt-0.5">{SITE.addressLines[1]}</div>
                  </div>
                </div>
              </a>
              <InfoRow
                icon={Phone}
                label="Phone"
                value={SITE.phoneDisplay}
                testId="info-phone"
              />
              <InfoRow
                icon={Mail}
                label="Email"
                value={SITE.email}
                testId="info-email"
              />
              <InfoRow
                icon={MessageCircle}
                label="WhatsApp"
                value={SITE.phoneDisplay}
                testId="info-whatsapp"
              />
            </div>

            {/* Hours */}
            <div className="mt-10 p-6 border border-[#0A0A0A]">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-5 h-5" />
                <span className="font-mono-ez text-xs font-bold uppercase tracking-widest">
                  Store Hours
                </span>
              </div>
              <ul className="space-y-2">
                {HOURS.map((h) => (
                  <li
                    key={h.day}
                    className="flex justify-between items-baseline gap-4 text-sm"
                  >
                    <span className="font-medium">{h.day}</span>
                    <span className="font-mono-ez text-[#525252]">{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ez btn-ez-primary"
                data-testid="location-whatsapp-cta"
              >
                Chat on WhatsApp
              </a>
            </div>
          </div>

          {/* Right: Map */}
          <div className="lg:col-span-7">
            <div className="relative aspect-[4/3] lg:aspect-auto lg:h-full min-h-[460px] border border-[#0A0A0A] overflow-hidden">
              <iframe
                title="EZ Device store location"
                src={SITE.googleMapsEmbedSrc}
                className="absolute inset-0 w-full h-full"
                style={{ border: 0, filter: "grayscale(0.2) contrast(1.05)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="google-map-embed"
              />
              <a
                href={SITE.googleMapsShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-4 right-4 btn-ez btn-ez-dark"
                data-testid="open-in-maps-btn"
              >
                Open in Google Maps →
              </a>
              <div className="absolute top-4 left-4 ez-chip bg-white">
                <MapPin className="w-3 h-3 text-[#FF3333]" />
                EZ Device · Flagship store
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function InfoRow({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex gap-4" data-testid={testId}>
      <div className="w-10 h-10 border border-[#0A0A0A] flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4" strokeWidth={2} />
      </div>
      <div>
        <div className="font-mono-ez text-[11px] uppercase tracking-widest text-[#525252]">
          {label}
        </div>
        <div className="font-medium text-base whitespace-pre-line mt-1">
          {value}
        </div>
      </div>
    </div>
  );
}
